import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles, withTheme } from '@glu/theming';
import locale from '@glu/locale';
import { DatePicker } from '@glu/datepicker-react';
import moment from 'moment';
import {
  CheckboxRadioGroup, CheckboxRadio, NumberInput
} from '@glu/form-components';
import SchedulerContext from './SchedulerContext';
import {
  getComplexTypeByValue, END_DATE, FURTHER_NOTICE, OCCURRENCE
} from './utils/end';
import styles from './End.styles';
import './themeDefaults';

export const SIMPLE = 'simple';
export const NONE = 'none';
export const COMPLEX = 'complex';

const End = ({
  classes,
  type,
  endValue,
  endsComplexOptions,
  startValue,
  setEndValue,
  DatePickerProps,
  error
}) => {
  const {
    dataQa, htmlId, disabled, dateFormat
  } = useContext(SchedulerContext);
  const occurrenceText = locale.get('schedulerReact.afterXOccurences').split('{0}'); // Input sits within text, so split on {0} placeholder
  const [complexType, setComplexType] = useState(getComplexTypeByValue(endValue));
  const [occurrences, setOccurrences] = useState(complexType === OCCURRENCE ? endValue : 1);
  const [localEndValue, setLocalEndValue] = useState(moment.isMoment(endValue)
    ? endValue
    : null);

  const datePickerProps = {
    value: [localEndValue, localEndValue],
    dateMin: startValue ? moment(startValue).format('YYYY-MM-DD') : undefined,
    format: dateFormat,
    ...DatePickerProps
  };

  const handleComplexTypeChange = (name, value) => {
    if (disabled || complexType === value) return;
    setComplexType(value);
    switch (value) {
      case OCCURRENCE:
        setEndValue(occurrences);
        break;
      case END_DATE:
        setEndValue(localEndValue);
        break;
      default:
        setEndValue(null);
        break;
    }
  };

  const handleOccurrenceChange = (name, value) => {
    setOccurrences(value);
    setEndValue(value);
  };

  const handleComplexEndDate = (value) => {
    setLocalEndValue(moment(value));
    setEndValue(value);
  };

  return (
    <div data-qa={`end-${dataQa || htmlId}`}>
      {type === SIMPLE && (
        <DatePicker
          htmlId={`${htmlId}-simpleEndPicker`}
          singleDatePicker
          labelText={locale.get('schedulerReact.endDate')}
          disabled={disabled}
          onChange={(name, values) => setEndValue(values[0])}
          error={error}
          {...datePickerProps}
        />
      )}
      {type === COMPLEX && (
      <CheckboxRadioGroup format="block" groupLabel={locale.get('schedulerReact.ends')} groupLabelClass={classes.radioLegend}>
        {endsComplexOptions.includes(FURTHER_NOTICE) && (
          endsComplexOptions.length > 1 ? (
            <CheckboxRadio
              htmlId={`${htmlId}-further`}
              className={classes.radio}
              name="complexOptions"
              data-qa="end-complex-radio"
              htmlValue={FURTHER_NOTICE}
              checked={complexType === FURTHER_NOTICE}
              type="radio"
              labelText={locale.get('schedulerReact.never')}
              disableInputAPI
              onChange={() => handleComplexTypeChange('complexOptions', FURTHER_NOTICE)}
            />
          ) : <div data-qa="end-complex-label">{locale.get('schedulerReact.never')}</div>
        )}
        {endsComplexOptions.includes(OCCURRENCE) && (
          <div
            className={classes.occurrences}
            onClick={() => handleComplexTypeChange('complexOptions', OCCURRENCE)}
            role="presentation"
            type="button"
          >
            {endsComplexOptions.length > 1 && (
              <CheckboxRadio
                htmlId={`${htmlId}-occurrences`}
                className={classes.radioLabel}
                name="complexOptions"
                data-qa="end-complex-radio"
                htmlValue={OCCURRENCE}
                checked={complexType === OCCURRENCE}
                type="radio"
                labelText={`${occurrenceText[0]} ${occurrenceText[1]}`}
                screenReaderLabel
                inline
                disableInputAPI
                onChange={handleComplexTypeChange}
              />
            )}
            <div className={classes.occurrenceLabel}>
              <span className={classes.occurrenceText}>{occurrenceText[0]}</span>
              <NumberInput
                htmlId={`${htmlId}-occurrenceText`}
                name={`${htmlId}-occurrenceText`}
                className={classes.occurrenceInput}
                labelText={`${occurrenceText[0]} ${occurrenceText[1]}`}
                screenReaderLabel
                min={1}
                inline
                value={occurrences}
                disabled={complexType !== OCCURRENCE || disabled}
                disableInputAPI
                onChange={handleOccurrenceChange}
              />
              <span className={classes.occurrenceText}>{occurrenceText[1]}</span>
            </div>
          </div>
        )}
        {endsComplexOptions.includes(END_DATE) && (
          <div
            className={classes.endSelected}
            onClick={() => handleComplexTypeChange('complexOptions', END_DATE)}
            role="presentation"
            type="button"
          >
            {endsComplexOptions.length > 1 && (
              <CheckboxRadio
                htmlId={`${htmlId}-selected`}
                className={classes.radioLabel}
                name="complexOptions"
                data-qa="end-complex-radio"
                type="radio"
                labelText={locale.get('schedulerReact.endSelected')}
                screenReaderLabel
                inline
                htmlValue={END_DATE}
                checked={complexType === END_DATE}
                disableInputAPI
                onChange={handleComplexTypeChange}
              />
            )}
            <span>{locale.get('schedulerReact.endSelected')}</span>
            <div className={classes.complexDatepicker}>
              <DatePicker
                htmlId={`${htmlId}-complexEndPicker`}
                singleDatePicker
                labelText={locale.get('schedulerReact.endSelected')}
                screenReaderLabel
                onChange={(name, values) => handleComplexEndDate(values[0])}
                disabled={complexType !== END_DATE || disabled}
                disableInputAPI
                {...datePickerProps}
              />
            </div>
          </div>
        )}
      </CheckboxRadioGroup>
      )}
    </div>
  );
};
End.propTypes = {
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** Complex End Options */
  endsComplexOptions: PropTypes.arrayOf(
    PropTypes.oneOf([OCCURRENCE, END_DATE, FURTHER_NOTICE])
  ),

  /** End value given, either a Date or a number for occurrences */
  endValue: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.number]),

  /** Callback to set end value on scheduler */
  setEndValue: PropTypes.func.isRequired,

  /** Starting value, used to limit the earliest end date */
  startValue: PropTypes.instanceOf(Object),

  /** Type of end value to set */
  type: PropTypes.oneOf([NONE, SIMPLE, COMPLEX]).isRequired,

  /** Passthrough props for datepicker */
  DatePickerProps: PropTypes.objectOf(PropTypes.shape({})),

  /** Error from validation */
  error: PropTypes.string
};

End.defaultProps = {
  endValue: undefined,
  startValue: undefined,
  endsComplexOptions: [FURTHER_NOTICE, OCCURRENCE, END_DATE],
  DatePickerProps: {},
  error: undefined
};

export const BareEndModule = End;
export default withTheme(withStyles(styles)(End));
